import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, defaultRoute } from "@/lib/auth";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { NAV, NAV_BY_TO, GROUP_LABEL, DEFAULT_MOBILE_TABS, canAccess } from "@/lib/nav";
import WhatsNewBanner from "@/components/WhatsNewBanner";
import { syncPushSubscription } from "@/lib/pushSync";
import {
  LayoutDashboard, LogOut, Menu, X, KeyRound, Settings, ChevronRight,
} from "lucide-react";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { GlobalSearch } from "@/components/GlobalSearch";
import { AIAssistant } from "@/components/AIAssistant";
import { NotificationBell } from "@/components/NotificationBell";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { PreferencesDialog } from "@/components/PreferencesDialog";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Re-sync this browser's push subscription with the server once per load —
  // heals cases where the server lost the record (user would otherwise see
  // "No push subscription registered" and silently stop receiving pushes).
  useEffect(() => { syncPushSubscription(); }, []);

  const groups = ["clients", "main", "ops", "management", "crm", "settings"];

  return (
    // Iter-76: use `100dvh` (dynamic viewport height) so iOS Safari does NOT
    // measure the layout against the "large viewport" (which includes the
    // collapsible URL bar). If we stuck with `100vh`, the fixed bottom tab
    // bar would appear to drift into the middle of the page when the URL
    // bar collapses during scroll. `100dvh` recalculates automatically.
    // `min-h-screen` is kept as a fallback for old browsers.
    <div className="min-h-screen min-h-[100dvh] bg-[hsl(var(--background))] flex">
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen h-[100dvh] w-64 bg-slate-900 text-slate-100 transition-transform duration-200 flex flex-col`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-14 h-14 object-contain drop-shadow-[0_0_10px_rgba(6,182,212,0.4)]" />
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-base leading-none">CCFI <span className="text-cyan-400">Compass</span></div>
              <div className="text-[10px] text-slate-400 stamp-label mt-1">Central Coast Fencing</div>
            </div>
          </div>
          <button data-testid="sidebar-close-btn" className="lg:hidden text-slate-400 hover:text-white" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {groups.map((g) => {
            const items = NAV.filter((n) => n.group === g).filter((n) => !n.superOnly || user?.role === "super_admin").filter((n) => !n.adminOnly || ["admin", "super_admin"].includes(user?.role)).filter((n) => !n.approvals || user?.quote_approver || user?.role === "super_admin" || ["quotes", "orders"].some((p) => (user?.permissions || []).includes(p))).filter((n) => user?.role === "super_admin" || !n.perm || (user?.permissions || []).includes(n.perm));
            if (items.length === 0) return null;
            return (
            <div key={g}>
              <div className="stamp-label text-slate-500 px-2 mb-2">{GROUP_LABEL[g]}</div>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors group ${
                        isActive
                          ? "bg-cyan-600 text-white"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`
                    }
                  >
                    <item.icon size={16} strokeWidth={1.75} />
                    <span>{item.label}</span>
                    {item.to === "/approvals" && <ApprovalsNavBadge />}
                    {item.to === "/deals" && <DealsNavBadge />}
                    {item.to === "/orders" && <OrdersNavBadge />}
                    {item.to === "/jobs" && <JobsNavBadge />}
                  </NavLink>
                ))}
              </div>
            </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 p-3">
          <div className="flex items-center justify-between gap-2 px-2 py-2">
            <div className="w-9 h-9 bg-slate-700 flex items-center justify-center font-display font-semibold text-white text-sm" title={`${user?.name || ""} · ${user?.email || ""}`} data-testid="user-avatar">
              {(user?.name || "U").split(" ").map(p=>p[0]).join("").slice(0,2).toUpperCase()}
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="preferences-btn" onClick={() => setPrefsOpen(true)} className="text-slate-400 hover:text-cyan-400 p-1.5" title="Preferences">
                <Settings size={16} />
              </button>
              <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="text-slate-400 hover:text-cyan-400 p-1.5" title="Change password">
                <KeyRound size={16} />
              </button>
              <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-cyan-400 p-1.5" title="Sign out">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <PreferencesDialog open={prefsOpen} onOpenChange={setPrefsOpen} />

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <button data-testid="sidebar-open-btn" onClick={() => setOpen(true)} className="text-slate-700"><Menu size={22} /></button>
          <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-10 h-10 object-contain" />
          <div className="flex-1 min-w-0"><GlobalSearch /></div>
          <NotificationBell />
        </header>
        {/* Desktop top bar with global search */}
        <header className="hidden lg:flex sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 px-10 py-3 items-center">
          <GlobalSearch className="w-[440px]" />
          <div className="ml-auto"><NotificationBell /></div>
        </header>
        <div className="flex-1 overflow-x-hidden pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          <WhatsNewBanner />
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar — hidden on lg+ */}
      <MobileTabBar user={user} />
      <AIAssistant />

      {/* PWA install prompt (mobile only, dismissible) */}
      <PWAInstallPrompt />
    </div>
  );
}

function MobileTabBar({ user }) {
  // "Home" points at the user's first accessible page. Users can pick up to
  // 4 personal shortcuts (Preferences → Mobile shortcuts); empty = default set.
  const isSuper = user?.role === "super_admin";
  const perms = user?.permissions || [];
  const home = defaultRoute(user);
  const { data: myPrefs } = useQuery({
    queryKey: ["me-preferences"],
    queryFn: async () => (await api.get("/auth/me/preferences")).data,
    staleTime: 60000,
  });
  const picks = (myPrefs?.mobile_tabs || [])
    .map((to) => NAV_BY_TO[to])
    .filter(Boolean)
    .filter((n) => canAccess(user, n))
    .slice(0, 4)
    .map((n) => ({ to: n.to, label: n.short || n.label, icon: n.icon }));
  const base = picks.length
    ? [{ to: home, label: "Home", icon: LayoutDashboard, home: true }, ...picks]
    : DEFAULT_MOBILE_TABS
        .map((t) => (t.home ? { ...t, to: home } : t))
        .filter((t) => t.home || isSuper || !t.perm || perms.includes(t.perm));
  const seen = new Set();
  const tabs = base
    .map((t) => (t.home ? { ...t, to: home } : t))
    .filter((t) => (seen.has(t.to) ? false : (seen.add(t.to), true)));
  return (
    <nav
      data-testid="mobile-tabbar"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(15,23,42,0.06)]"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        // Iter-76: iOS Safari sometimes lets `position: fixed` drift up
        // during momentum scroll or URL-bar collapse. Force GPU compositing
        // via a 3D transform + will-change so the bar stays pinned to the
        // visual viewport bottom instead of the layout viewport bottom.
        transform: "translate3d(0, 0, 0)",
        WebkitTransform: "translate3d(0, 0, 0)",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        // Prevent overscroll on the body from dragging the fixed bar with it.
        overscrollBehavior: "contain",
      }}
    >
      <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`mtab-${t.to.replace(/^\//, "").replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center h-14 gap-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                  t.primary
                    ? isActive
                      ? "text-white bg-cyan-600"
                      : "text-cyan-600 hover:bg-cyan-50"
                    : isActive
                      ? "text-cyan-700"
                      : "text-slate-500 hover:text-slate-900"
                } ${t.primary ? "relative" : ""}`
              }
            >
              {t.primary ? (
                <div className="absolute -top-4 w-11 h-11 bg-cyan-600 border-4 border-white rounded-full flex items-center justify-center shadow-md">
                  <Icon size={19} className="text-white" />
                </div>
              ) : (
                <Icon size={18} strokeWidth={2} />
              )}
              <span className={t.primary ? "mt-6" : ""}>{t.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

// Breadcrumb labels → routes (segments with a known route become clickable)
const CRUMB_ROUTES = {
  "Dashboard": "/dashboard",
  "Clients": "/clients",
  "Directory": "/clients",
  "New Enquiry": "/enquiry/new",
  "Companies": "/companies",
  "Contacts": "/contacts",
  "Materials": "/materials",
  "Suppliers": "/suppliers",
  "Daily Tasks": "/tasks",
  "Deals": "/deals",
  "Quotes": "/quotes",
  "Activities": "/activities",
  "Scheduler": "/scheduler",
  "Jobs": "/jobs",
  "Fabrication": "/fabrication",
  "Calculator": "/fabrication/calculator",
  "Blueprints": "/blueprints",
  "Measure-Up Pack": "/measure-up",
  "Purchase Orders": "/orders",
  "Stock Take": "/stock",
  "Automation": "/automation",
  "Email": "/email",
  "Integrations": "/integrations",
  "Profit": "/profit",
  "Reports": "/reports",
  "Staff & Roles": "/team",
  "Audit Log": "/audit",
};

export function PageHeader({ title, subtitle, actions, breadcrumb, hideBack }) {
  const nav = useNavigate();
  const location = useLocation();
  // Hide back on the top-level dashboard (there's nothing to go back to)
  const isDashboard = location.pathname === "/dashboard" || location.pathname === "/";
  const showBack = !hideBack && !isDashboard;
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
        {(breadcrumb || showBack) && (
          <div className="flex items-center gap-2 mb-3">
            {showBack && (
              <button
                data-testid="page-back-btn"
                onClick={() => (window.history.length > 1 ? nav(-1) : nav("/dashboard"))}
                className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-cyan-600 border border-slate-200 hover:border-cyan-400 bg-white rounded-sm px-2 py-1 transition-colors"
                aria-label="Go back"
              >
                <ChevronRight size={12} className="rotate-180" /> Back
              </button>
            )}
            {breadcrumb && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 font-medium">
                {breadcrumb.map((b, i) => {
                  const isLast = i === breadcrumb.length - 1;
                  const route = !isLast ? CRUMB_ROUTES[b] : null;
                  return (
                    <span key={`crumb-${b}-${i}`} className="flex items-center gap-1">
                      {i > 0 && <ChevronRight size={12} />}
                      {route ? (
                        <button
                          type="button"
                          onClick={() => nav(route)}
                          data-testid={`crumb-${String(b).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="hover:text-cyan-600 hover:underline underline-offset-2 transition-colors"
                        >
                          {b}
                        </button>
                      ) : (
                        <span className={isLast ? "text-slate-900" : ""}>{b}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 truncate" data-testid="page-title">{title}</h1>
            {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-1 sm:mt-1.5 max-w-2xl">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
      </div>
    </div>
  );
}

function ApprovalsNavBadge() {
  const { data } = useQuery({
    queryKey: ["approvals-pending"],
    queryFn: async () => (await api.get("/approvals/pending")).data,
    refetchInterval: 60000,
  });
  // Unread (new submissions + chat replies), not total pending (Trent, Jun 2026).
  const n = data?.unread_total || 0;
  if (!n) return null;
  return (
    <span className="ml-auto bg-amber-400 text-slate-900 text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="approvals-nav-badge">{n}</span>
  );
}

function DealsNavBadge() {
  const { data } = useQuery({
    queryKey: ["deals-unread"],
    queryFn: async () => (await api.get("/deals/unread/summary")).data,
    refetchInterval: 60000,
  });
  const n = data?.total || 0;
  if (!n) return null;
  return (
    <span className="ml-auto bg-amber-400 text-slate-900 text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="deals-nav-badge">{n}</span>
  );
}

function OrdersNavBadge() {
  const { data } = useQuery({
    queryKey: ["orders-unread"],
    queryFn: async () => (await api.get("/orders/unread/summary")).data,
    refetchInterval: 60000,
  });
  const n = data?.total || 0;
  if (!n) return null;
  return (
    <span className="ml-auto bg-amber-400 text-slate-900 text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="orders-nav-badge">{n}</span>
  );
}

function JobsNavBadge() {
  const { data } = useQuery({
    queryKey: ["jobs-unread"],
    queryFn: async () => (await api.get("/jobs/unread/summary")).data,
    refetchInterval: 60000,
  });
  const n = data?.total || 0;
  if (!n) return null;
  return (
    <span className="ml-auto bg-amber-400 text-slate-900 text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="jobs-nav-badge">{n}</span>
  );
}
